/**
 * Navigationsmodell (D-M0-K3 Struktur, D-M0-K4 Icon-Vertrag). Reine Daten ohne
 * React, damit der Duplikat-Test (navModel.test.ts) die Registry leichtgewichtig
 * prüfen kann. Die SVG-Pfade liegen zentral in navIcons.tsx; hier werden Icons
 * nur über ihren Namen referenziert (kein Inline-SVG in der Sidebar).
 */
import type { UserResponseDtoOutputPermissions } from '@werkbank/shared';
import { SETTINGS_AREAS } from '../lib/settingsNav';

/**
 * Erlaubte Werte für `NavItem.flag`. Bewusst an die generierten Permission-
 * Flags gebunden: ein Tippfehler wäre sonst kein Übersetzungsfehler, sondern
 * ein Eintrag, den die Sidebar stillschweigend für alle ausblendet (Faruq
 * 2026-08-29: „Zahlungseingänge" fehlte genau so).
 */
export type NavFlag = keyof UserResponseDtoOutputPermissions;

export type NavItem = {
  to: string;
  label: string;
  icon: string;
  adminOnly?: boolean;
  /** Sichtbar nur, wenn der angemeldete Nutzer diesen Permission-Flag hält. */
  flag?: NavFlag;
  /**
   * W2-B5 (Faruq): Unterpunkte in der Sidebar (z. B. die Einstellungs-
   * Bereiche). Sie rendern nur im ausgeklappten Zustand und nur, wenn die
   * Route des Elternteils aktiv ist (Auto-Expand); `tab` ist der
   * Routen-Parameter unterhalb von `to`.
   */
  children?: { tab: string; label: string }[];
};
export type NavSection = { label: string | null; items: NavItem[] };

/**
 * Sektionen entlang des Arbeitsflusses; jedes Ziel ein Klick (K3). Jeder Eintrag
 * trägt ein eindeutiges, objektbezogenes Symbol (K4): kein Symbol doppelt, auch
 * nicht über Sektionen hinweg.
 */
export const NAV_SECTIONS: NavSection[] = [
  // Faruq 2026-09-25: die Alltags-Punkte in fester Reihenfolge ganz oben (ohne
  // Überschrift), alles Weitere darunter unter „Weitere Funktionen"; der
  // System-Block bleibt unverändert.
  {
    label: null,
    items: [
      { to: '/', label: 'Übersicht', icon: 'dashboard' },
      { to: '/plantafel', label: 'Plantafel', icon: 'plantafel' },
      { to: '/orders', label: 'Aufträge', icon: 'orders' },
      // Tobias 2026-08-29: eigener Arbeitsablauf (Material melden → bestellen →
      // Wareneingang), deshalb ein eigener Punkt statt eines Filters in „Aufträge".
      { to: '/material', label: 'Bestellungen', icon: 'material' },
      { to: '/attendance', label: 'Arbeitszeit', icon: 'attendance' },
      // P1-F §2: vollständiges GoBD-Journal und einziger Ort auftragsloser Belege.
      { to: '/documents', label: 'Belegjournal', icon: 'documents' },
      { to: '/open-items', label: 'Offene Posten', icon: 'openItems' },
      { to: '/quotes', label: 'Angebote', icon: 'quotes' },
    ],
  },
  {
    label: 'Weitere Funktionen',
    items: [
      { to: '/recurring', label: 'Wiederkehrend', icon: 'recurring' },
      // Faruq 2026-08-29: Kontoauszüge von Sparkasse und Commerzbank einlesen;
      // eindeutige Zahlungen werden verbucht, der Rest geht als Prüffall ans
      // Büro. Recht wie die Erfassung von Hand.
      {
        to: '/payments-in',
        label: 'Zahlungseingänge',
        icon: 'bankIncoming',
        flag: 'recordPayments',
      },
      { to: '/dunning', label: 'Mahnwesen', icon: 'dunning' },
      { to: '/analytics', label: 'Auswertungen', icon: 'analytics' },
      { to: '/customers', label: 'Kunden', icon: 'customers' },
      { to: '/articles', label: 'Artikel', icon: 'articles' },
      { to: '/price-lists', label: 'Preislisten', icon: 'priceLists' },
    ],
  },
  {
    label: 'System',
    items: [
      // Faruq 2026-08-17: „Datenaustausch" und „Aktivitäten" ziehen ans
      // untere Ende zu den systemnahen Werkzeugen (vormals Abrechnung/Betrieb).
      // Kein Ticket-Eintrag hier: er sitzt unten bei der Hilfe und wechselt
      // dort nur seine Beschriftung (Faruq 2026-08-24) — „Tickets" fuer die
      // Systembetreuung, „Support" fuer alle anderen. EINE Stelle, damit
      // niemand zwei Tueren zum selben Raum sucht.
      { to: '/datenaustausch', label: 'Datenaustausch', icon: 'exchange' },
      { to: '/activity', label: 'Aktivitäten', icon: 'activity', flag: 'viewAnalytics' },
      { to: '/users', label: 'Benutzer', icon: 'users', adminOnly: true },
      // Frei editierbare Rollen (Faruq 2026-08-20): eigener Eintrag unter Benutzer.
      { to: '/roles', label: 'Rollen', icon: 'roles', adminOnly: true },
      // P2-M12: gerätneutrale Geräteverwaltung — gekoppelte Monteur-App-Geräte.
      { to: '/devices', label: 'Geräte', icon: 'devices', adminOnly: true },
      // W2-B5: die Einstellungs-Bereiche hängen als Untermenü direkt an der
      // Sidebar (vormals eigene Nav-Spalte auf der Einstellungen-Seite).
      {
        to: '/settings',
        label: 'Einstellungen',
        icon: 'settings',
        adminOnly: true,
        children: SETTINGS_AREAS.map((a) => ({ tab: a.value, label: a.label })),
      },
    ],
  },
];
